import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { getSession, onAuthStateChange, signOut, invokeEdgeFunction } from "@/api/auth";
import { fetchStoresByOwner, fetchStoreSummaryById } from "@/api/stores";
import { fetchEmployeeByUserId } from "@/api/employees";
import { fetchUserRoleMaybe, fetchProfile } from "@/api/profiles";
import {
  fetchSupportAdminRow,
  fetchMyActiveSupportSession,
  type SupportSessionRow,
} from "@/api/support";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "@supabase/supabase-js";
import type { Enums } from "@/integrations/supabase/types";
import { capturePosthog, identifyPosthog, POSTHOG_OPT_IN_EVENT } from "@/lib/posthog";
import { setSentryUser, setSentryContext } from "@/lib/sentry";
import { logger } from "@/lib/logger";
import { reportError } from "@/lib/reportError";
import { canUseCrisp, disableCrisp, enableCrisp } from "@/lib/crisp";
import { clearAccountScopedCaches } from "@/lib/sessionCleanup";
import { assuranceLevelOfAccessToken, type AssuranceLevel } from "@/lib/authAssurance";
import { PageLoader } from "@/components/ui/page-loader";
import { AccountSetupError } from "@/components/AccountSetupError";
import { SessionLoadError } from "@/components/SessionLoadError";

interface Store {
  id: string;
  name: string;
  subscription_status?: string;
  creation_source?: string;
}

/** First store at signup: explicit signup row, else oldest (created_at order from API). */
function defaultOwnerStoreId(stores: Store[]): string {
  const signup = stores.find((s) => s.creation_source === "signup");
  return signup?.id ?? stores[0]!.id;
}

interface Profile {
  first_name: string;
  last_name: string;
}

interface StoreContextType {
  user: User | null;
  profile: Profile | null;
  stores: Store[];
  selectedStore: string | null;
  setSelectedStore: (storeId: string) => void;
  reloadStores: () => Promise<void>;
  reloadProfile: () => Promise<void>;
  isLoading: boolean;
  userRole: Enums<"app_role"> | null;
  handleLogout: () => Promise<void>;
  /** Present when the signed-in user is allowlisted in support_admins */
  supportAdminLabel: string | null;
  /** Active support session where this user is the support actor (not null ⇒ acting in target store) */
  activeSupportActingSession: SupportSessionRow | null;
  /**
   * Niveau du jeton : `aal1` après le mot de passe, `aal2` après le code de
   * double authentification. Exigé du seul mode support (SupportMfaGate).
   */
  authAssuranceLevel: AssuranceLevel | null;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);
const CRISP_WEBSITE_ID = "58834b46-3fbc-45c3-a132-7aa3d5786367";

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<Enums<"app_role"> | null>(null);
  const [supportAdminLabel, setSupportAdminLabel] = useState<string | null>(null);
  const [activeSupportActingSession, setActiveSupportActingSession] = useState<SupportSessionRow | null>(null);
  const [authAssuranceLevel, setAuthAssuranceLevel] = useState<AssuranceLevel | null>(null);
  const [accountSetupError, setAccountSetupError] = useState(false);
  // Lecture de `user_roles` en échec : le rôle est *inconnu*, ce qui n'est pas
  // la même chose qu'absent. Voir `loadUserData`.
  const [sessionLoadError, setSessionLoadError] = useState(false);
  // Une seule tentative de réparation par session pour éviter les boucles.
  const repairAttemptedRef = useRef(false);

  const loadStoresForUser = useCallback(
    async (userId: string, role: Enums<"app_role"> | null) => {
      const { data: saRow } = await fetchSupportAdminRow(userId);
      const label = saRow?.label ?? null;
      setSupportAdminLabel(label);

      const { data: activeSupport } = await fetchMyActiveSupportSession(userId);
      const session = (activeSupport as SupportSessionRow | null) ?? null;
      setActiveSupportActingSession(session);

      if (session?.store_id) {
        const { data: st } = await fetchStoreSummaryById(session.store_id);

        if (st) {
          setStores([st as Store]);
          setSelectedStore(st.id);
          localStorage.setItem("selectedStore", st.id);
        }
        return;
      }

      // Support admin without an active session: idle, no stores. Don't sign out.
      if (label && !session) {
        setStores([]);
        setSelectedStore(null);
        return;
      }

      // `role === null` signifie ici « aucune ligne dans user_roles », jamais
      // « lecture impossible » : `loadUserData` s'arrête avant d'appeler cette
      // fonction quand la lecture échoue. C'est ce qui rend le `!role`
      // ci-dessous légitime — sans cette garantie, il envoie dans la branche
      // franchisé, et donc sur `repair-account`, tout utilisateur dont la
      // requête de rôle a échoué.
      if (!role || role === "franchisee") {
        let { data: storesData } = await fetchStoresByOwner(userId);

        // Compte incomplet (provisioning signup en échec) : tentative de
        // réparation automatique unique, puis re-lecture.
        if ((!storesData || storesData.length === 0) && !repairAttemptedRef.current) {
          repairAttemptedRef.current = true;
          const { data: repairData, error: repairError } = await invokeEdgeFunction("repair-account", {});
          if (repairError) {
            reportError(repairError, "repair-account", { userId });
          } else {
            const repaired = (repairData as { repaired?: string[] } | null)?.repaired ?? [];
            if (repaired.length > 0) {
              reportError(new Error("Compte incomplet réparé automatiquement"), "repair-account", {
                userId,
                repaired: repaired.join(","),
              });
            }
          }
          ({ data: storesData } = await fetchStoresByOwner(userId));
        }

        if (storesData && storesData.length > 0) {
          setAccountSetupError(false);
          setStores(storesData);
          const savedStore = localStorage.getItem("selectedStore");
          const fallbackId = defaultOwnerStoreId(storesData);
          if (savedStore && storesData.some((s) => s.id === savedStore)) {
            setSelectedStore(savedStore);
          } else {
            setSelectedStore(fallbackId);
            localStorage.setItem("selectedStore", fallbackId);
          }
        } else {
          setStores([]);
          setSelectedStore(null);
          // Toujours aucun magasin après réparation : écran d'erreur
          // explicite plutôt qu'un loader infini.
          setAccountSetupError(true);
          reportError(new Error("Franchisé sans magasin après tentative de réparation"), "store-context", {
            userId,
          });
        }
      } else {
        const { data: empData } = await fetchEmployeeByUserId(userId);

        if (empData?.store_id) {
          const store = empData.stores as unknown as { id: string; name: string };
          if (store) {
            setStores([store]);
            setSelectedStore(store.id);
          }
        } else {
          logger.error("No employee record found for manager role, signing out");
          await signOut();
          await clearAccountScopedCaches(queryClient);
          navigate("/auth");
        }
      }
    },
    [navigate, queryClient],
  );

  const loadUserData = useCallback(
    async (userId: string) => {
      try {
        // `maybeSingle` et non `single` : sans lui, l'absence de ligne
        // remonte comme une erreur (PGRST116) et devient indiscernable d'une
        // panne réseau. Or les deux appellent des réponses opposées — réparer
        // le compte dans un cas, ne rien décider dans l'autre.
        const { data: roleData, error: roleError } = await fetchUserRoleMaybe(userId);

        if (roleError) {
          // Rôle indéterminé. Poursuivre reviendrait à le deviner : `!role`
          // dans `loadStoresForUser` bascule dans la branche franchisé, ce qui
          // laissait un salarié sur /dashboard et déclenchait `repair-account`
          // pour rien. Une coupure réseau n'est pas une réponse d'autorisation.
          reportError(roleError, "store-context", { userId, phase: "fetch-user-role" });
          setSessionLoadError(true);
          return;
        }

        setSessionLoadError(false);

        if (roleData) {
          setUserRole(roleData.role);

          if (roleData.role === "employee") {
            navigate("/employee");
            setIsLoading(false);
            return;
          }
        }

        const { data: profileData } = await fetchProfile(userId);

        if (profileData) {
          setProfile(profileData);
        }

        await loadStoresForUser(userId, roleData?.role ?? null);
      } catch (error) {
        logger.error("Error loading user data", error);
      } finally {
        setIsLoading(false);
      }
    },
    [loadStoresForUser, navigate],
  );

  useEffect(() => {
    if (userRole && userRole !== "franchisee") return;
    if (stores.length === 0) return;
    const ids = new Set(stores.map((s) => s.id));
    if (selectedStore && ids.has(selectedStore)) return;
    const id = defaultOwnerStoreId(stores);
    setSelectedStore(id);
    localStorage.setItem("selectedStore", id);
  }, [stores, selectedStore, userRole]);

  useEffect(() => {
    const {
      data: { subscription },
    } = onAuthStateChange(async (event, session) => {
      // Lu dans le jeton, sans le SDK : un appel au SDK se bloque dans ce
      // callback. Après la saisie du code, `MFA_CHALLENGE_VERIFIED` apporte la
      // session aal2 et SupportMfaGate s'ouvre.
      setAuthAssuranceLevel(assuranceLevelOfAccessToken(session?.access_token));
      if (!session?.user) {
        navigate("/auth");
      } else {
        setUser(session.user);
      }
    });

    getSession().then(({ data: { session } }) => {
      setAuthAssuranceLevel(assuranceLevelOfAccessToken(session?.access_token));
      if (!session?.user) {
        navigate("/auth");
      } else {
        setUser(session.user);
        loadUserData(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, loadUserData]);

  useEffect(() => {
    if (!user?.id) return;

    const distinctId = user.id;
    const props = {
      role: userRole,
      store_id: selectedStore,
      support_mode: activeSupportActingSession ? activeSupportActingSession.access_mode : null,
    } as Record<string, unknown>;

    identifyPosthog(distinctId, props);
    setSentryUser({ id: distinctId, email: user.email ?? undefined, role: (userRole as string) ?? undefined });
    const storeName = stores.find((s) => s.id === selectedStore)?.name ?? null;
    setSentryContext("store", selectedStore ? { id: selectedStore, name: storeName } : null);

    const handleOptIn = () => {
      identifyPosthog(distinctId, props);
    };

    window.addEventListener(POSTHOG_OPT_IN_EVENT, handleOptIn);
    return () => window.removeEventListener(POSTHOG_OPT_IN_EVENT, handleOptIn);
  }, [user?.id, user?.email, userRole, selectedStore, activeSupportActingSession, stores]);

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      disableCrisp();
      return;
    }

    if (!canUseCrisp(userRole)) {
      disableCrisp();
      return;
    }

    const storeName = stores.find((store) => store.id === selectedStore)?.name ?? null;
    const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() || null;

    enableCrisp(CRISP_WEBSITE_ID, {
      email: user.email ?? null,
      displayName,
      role: userRole,
      storeName,
    });
  }, [isLoading, user, userRole, profile?.first_name, profile?.last_name, stores, selectedStore]);

  const handleStoreChange = (storeId: string) => {
    if (activeSupportActingSession) {
      return;
    }
    setSelectedStore(storeId);
    localStorage.setItem("selectedStore", storeId);
    capturePosthog("store_selected", { store_id: storeId, role: userRole });
  };

  const handleLogout = async () => {
    disableCrisp();
    setSentryUser(null);
    await signOut("global");
    await clearAccountScopedCaches(queryClient);
    navigate("/auth?logged_out=true", { replace: true });
  };

  const reloadStores = async () => {
    if (user?.id) {
      await loadStoresForUser(user.id, userRole);
    }
  };

  const reloadProfile = async () => {
    if (!user?.id) return;
    const { data: profileData } = await fetchProfile(user.id);
    if (profileData) {
      setProfile(profileData);
    }
  };

  return (
    <StoreContext.Provider
      value={{
        user,
        profile,
        stores,
        selectedStore,
        setSelectedStore: handleStoreChange,
        reloadStores,
        reloadProfile,
        isLoading,
        userRole,
        handleLogout,
        supportAdminLabel,
        activeSupportActingSession,
        authAssuranceLevel,
      }}
    >
      {isLoading ? (
        <PageLoader text="Chargement du magasin..." className="min-h-screen" />
      ) : sessionLoadError ? (
        <SessionLoadError onLogout={handleLogout} />
      ) : accountSetupError ? (
        <AccountSetupError onLogout={handleLogout} />
      ) : (
        children
      )}
    </StoreContext.Provider>
  );
};

export const useStoreContext = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStoreContext must be used within a StoreProvider");
  }
  return context;
};
